<script lang="ts" setup>
import { DocumentAdd, Download, Close } from "@element-plus/icons-vue";
import { ElNotification, ElMessageBox, UploadProps } from "element-plus";
import type { UploadFile } from "element-plus";
import { ref, onMounted, reactive } from "vue";
import { Attachment } from "types";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { FormRuleType } from "~/types/element-ui/FormRuleType";
import { useProductStore } from "~/stores/productStore";
import { Product } from "~/types/product";
import { useConfigStore } from "~/stores/configStore";
import { PermissionNames } from "~/configs/permission_names";
import { usePaymentItemStore } from "~/projects/crm/stores/paymentItemStore";
import { PaymentItem } from "~/projects/crm/types";
import { useSettingsStore } from "~/stores/settingsStore";
import { CategoryType } from "~/types/enums/categoryType";
import { Category } from "~/types/category";

definePageMeta({
  layout: false,
});

const props = defineProps({
  showEditModalHeader: {
    default: true,
    type: Boolean,
  },
});

// Emitted once the product exists and the store has been refreshed, so a host
// form can surface the new product without re-fetching or diffing the list.
const emit = defineEmits<{ (e: "created", product: Product): void }>();

const configStore = useConfigStore();
const productStore = useProductStore();
const paymentItemStore = usePaymentItemStore();
const settingsStore = useSettingsStore();

const showAddCategoryModal = ref(false);
const newCategoryName = ref("");

const cancelAddCategory = () => {
  showAddCategoryModal.value = false;
  newCategoryName.value = "";
};

const saveNewCategory = async () => {
  const name = newCategoryName.value.trim();
  if (!name) return;
  const response = await settingsStore.addCategory(CategoryType.product, name);
  if (response?.errors) {
    ElMessageBox.alert(response.message, "Warning", {
      confirmButtonText: "OK",
      showClose: false,
    });
    return;
  }
  await configStore.getConfig();
  const created = configStore.globalConfig.product_categories?.find(
    (category: Category) => category.name === name,
  );
  if (created) {
    formData.category_id = created.id;
  }
  ElNotification({
    title: "Updated",
    type: "success",
    message: "Product category added",
  });
  cancelAddCategory();
};

const allPaymentItems = ref<PaymentItem[]>([]);
const fileList = ref<UploadFile[]>([]);
const showViewer = ref(false);
const previewIndex = ref(0);

interface PendingRow {
  item_id: number | null;
  price: number;
  is_required: boolean;
}

const pendingRows = ref<PendingRow[]>([]);

const availableForRow = (rowIndex: number) =>
  allPaymentItems.value.filter(
    (i) =>
      !pendingRows.value.some(
        (r, idx) => idx !== rowIndex && r.item_id === i.id,
      ),
  );

const addPendingRow = () => {
  pendingRows.value.push({ item_id: null, price: 0, is_required: false });
};

const onPendingItemSelect = (index: number, id: number | null) => {
  if (id) {
    const item = allPaymentItems.value.find((i) => i.id === id);
    pendingRows.value[index].price = item ? Number(item.amount) : 0;
  } else {
    pendingRows.value[index].price = 0;
  }
};

const removePendingRow = (index: number) => {
  pendingRows.value.splice(index, 1);
};

const attachmentArray = ref([] as Attachment[]);
const attachmentUrl = ref("");
const showAttachmentModal = ref(false);
const formRef = ref<ElementUIFormRef | null>(null);
const formData = reactive<Partial<Product>>({} as Partial<Product>);

onMounted(async () => {
  allPaymentItems.value = await paymentItemStore.getAll();
});

const validateSku = async function (
  rule: FormRuleType,
  value: string,
  callback: (arg0?: Error) => void,
) {
  if (value !== "") {
    const exists = await productStore.skuExists(value);
    if (exists) {
      return callback(new Error("product id already exists"));
    }
  }
  callback();
};

const rules = reactive<FormRuleType>({
  sku: [
    {
      required: true,
      message: "product id is required",
      trigger: "blur",
    },
    {
      asyncValidator: validateSku,
      trigger: "blur",
    },
  ],
  name: [
    {
      required: true,
      message: "name is required",
      trigger: "blur",
    },
  ],
  quantity: [
    {
      required: true,
      message: "Quantity is required",
      trigger: "blur",
    },
  ],
  price: [
    {
      required: true,
      message: "price is required",
      trigger: "blur",
    },
  ],
});

const multipartFormData = new FormData();
const thumbnailRef = ref();

const handleThumbnailChange = (event: any) => {
  const file = event.target.files[0];
  multipartFormData.set("thumbnail", file);
};

const createProduct = function () {
  if (!formRef.value) return;
  formRef.value?.validate(async (valid: any) => {
    if (!valid) {
      ElNotification({
        title: "Failed",
        message: "Please provide all the required fields",
        type: "error",
      });
    } else if (valid) {
      formData.unique_identifiers = attachmentArray.value.map(
        (attachment) => attachment.id,
      );

      for (const [key, value] of Object.entries(formData)) {
        if (value !== null) {
          if (key === "unique_identifiers" && Array.isArray(value)) {
            value.forEach((item) => {
              multipartFormData.set("unique_identifiers[]", item);
            });
          } else {
            multipartFormData.set(key, value);
          }
        }
      }
      const missingName = fileList.value.some(
        (file) => !file.attachmentName?.trim(),
      );
      if (missingName) {
        ElNotification({
          title: "Name is required",
          message: "Please provide a name for each attachment.",
          type: "error",
        });
        return;
      }
      const attachments = fileList.value.map((file) => ({
        file: file.raw,
        name: file.attachmentName,
        description: file.description || "",
      }));
      const product = await productStore.createProduct(
        multipartFormData,
        attachments,
      );

      if (product) {
        for (const row of pendingRows.value.filter((r) => r.item_id !== null)) {
          await paymentItemStore.attachToProduct(
            product.data.id,
            row.item_id!,
            row.price,
            row.is_required,
          );
        }
        ElNotification({
          title: "Product created",
          message: "New product created",
          type: "success",
        });
      }

      await productStore.getProducts();
      await productStore.getAllProducts(productStore._simpleDir);

      if (product) {
        emit("created", product.data as Product);
      }

      productStore.setCreateModalVisibility(false);
    } else {
      console.log("error in validation");
    }
  });
};

const openAttachment = (url: string) => {
  attachmentUrl.value = url;
  window.open(attachmentUrl.value as unknown as string, "_blank");
};

const handleFileChange: UploadProps["onChange"] = (uploadFile) => {
  const exists = fileList.value.find((file) => file.uid === uploadFile.uid);

  if (!uploadFile.url && uploadFile.raw?.type.startsWith("image")) {
    uploadFile.url = URL.createObjectURL(uploadFile.raw);
  }
  if (!uploadFile.attachmentName) {
    uploadFile.attachmentName = uploadFile.name
      .replace(/\.[^/.]+$/, "")
      .split(" ")[0];
  }

  if (!exists) {
    fileList.value.push({
      ...uploadFile,
      attachmentName: uploadFile.name,
      description: "",
    });
  }
};
const removeFile = (index: number) => {
  const file = fileList.value[index];
  if (file.url) {
    URL.revokeObjectURL(file.url);
  } else if (file.raw) {
    URL.revokeObjectURL(URL.createObjectURL(file.raw));
  }
  fileList.value.splice(index, 1);
};
const getAcceptValue = () => {
  const accept =
    ".jpg, .jpeg, .png, .pdf,.ppt, .pptx, .xlx, .xlxs, .doc, .docx, .wav, .heic, .mp3, .mp4";
  return accept;
};
const handlePreview: UploadProps["onPreview"] = (file) => {
  const exists = fileList.value.find((f) => f.uid === file.uid);
  if (!exists) {
    if (!file.url && file.raw?.type.startsWith("image")) {
      file.url = URL.createObjectURL(file.raw);
    }
    fileList.value.push(file);
  }
};
</script>

<template>
  <EditModalHeader v-if="props.showEditModalHeader">
    <template #title>Create Product</template>
    <template #svg>
      <Svgs name="pen" />
    </template>
  </EditModalHeader>
  <el-form
    ref="formRef"
    :model="formData as Product"
    class="space-y-5"
    :rules="rules"
    @submit.prevent="createProduct"
  >
    <div class="flex flex-col gap-4 px-2">
      <el-form-item class="form-elem-container" prop="sku">
        <div class="form-elem-container">
          <FormLabel :required="true">Product Id</FormLabel>
          <el-input
            v-model="formData.sku"
            clearable
            placeholder="Please input product id"
          />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="industry">
        <div class="form-elem-container">
          <FormLabel>Category</FormLabel>
          <div class="flex items-center gap-2 w-full">
            <el-select
              v-model="formData.category_id"
              filterable
              class="flex-1"
              clearable
              placeholder="Please select category"
            >
              <el-option
                v-for="category in configStore.globalConfig.product_categories"
                :key="category.id"
                :label="category.name"
                :value="category.id"
              ></el-option>
            </el-select>
            <div
              v-if="isUserPermitted(PermissionNames.SETTING_MANAGE)"
              class="cursor-pointer shrink-0 flex items-center"
              @click.prevent="showAddCategoryModal = true"
            >
              <el-tooltip content="Add Product Category" placement="top">
                <Svgs
                  name="plus"
                  class="w-5 h-5 text-light-sec-purple dark:text-white"
                  stroke-width="1.2"
                />
              </el-tooltip>
            </div>
          </div>
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="name">
        <div class="form-elem-container">
          <FormLabel :required="true">Name</FormLabel>
          <el-input
            v-model="formData.name"
            clearable
            placeholder="Please input name"
          />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="quantity">
        <div class="form-elem-container">
          <FormLabel :required="true">Opening stock</FormLabel>
          <el-input
            v-model="formData.quantity"
            class="w-full"
            type="number"
            clearable
            placeholder="How many you have right now"
            @wheel="(e) => e.preventDefault()"
          />
        </div>
      </el-form-item>

      <!--
        Only on create. Once a product exists its count moves through the stock
        screen, where each change is recorded with a reason — see EditForm.
      -->
      <el-form-item class="form-elem-container" prop="cost_price">
        <div class="form-elem-container">
          <FormLabel>Cost price</FormLabel>
          <el-input
            v-model="formData.cost_price"
            class="w-full"
            type="number"
            clearable
            placeholder="What it cost you"
            @wheel="(e) => e.preventDefault()"
          />
          <div class="mt-1 text-xs opacity-60">
            Used to value your stock. Without it this product is left out of
            that total.
          </div>
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="low_stock_threshold">
        <div class="form-elem-container">
          <FormLabel>Tell me when stock falls to</FormLabel>
          <el-input
            v-model="formData.low_stock_threshold"
            class="w-full"
            type="number"
            clearable
            placeholder="Leave empty for no warning"
            @wheel="(e) => e.preventDefault()"
          />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="unit">
        <div class="form-elem-container">
          <FormLabel>Unit</FormLabel>
          <el-input
            v-model="formData.unit"
            class="w-full"
            clearable
            placeholder="pcs, kg, litre"
          />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="barcode">
        <div class="form-elem-container">
          <FormLabel>Barcode</FormLabel>
          <el-input
            v-model="formData.barcode"
            class="w-full"
            clearable
            placeholder="For scanning when goods arrive"
          />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="price">
        <div class="form-elem-container">
          <FormLabel :required="true">Price</FormLabel>
          <el-input
            v-model="formData.price"
            class="w-full"
            type="number"
            clearable
            placeholder="Please input price"
            @wheel="(e) => e.preventDefault()"
          />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="description">
        <div class="form-elem-container">
          <FormLabel>Description</FormLabel>
          <el-input
            v-model="formData.description"
            type="textarea"
            clearable
            placeholder="Place a more detailed description"
          />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="thumbnail">
        <div class="form-elem-container">
          <FormLabel>Thumbnail</FormLabel>
          <input
            ref="thumbnailRef"
            type="file"
            name="thumbnail"
            accept=".jpg, .jpeg, .png"
            @change="handleThumbnailChange"
          />
        </div>
      </el-form-item>

      <div v-if="attachmentArray.length > 0" class="pt-4">
        <ul>
          <li
            v-for="(item, index) in attachmentArray"
            :key="index"
            class="mb-2"
          >
            <div class="flex gap-3">
              <p class="text-gray-400 truncate w-10/12">
                {{ item.name }}
              </p>
              <Download
                class="w-6 h-6 text-blue-500 cursor-pointer"
                @click="openAttachment(item.public_url)"
              />
            </div>
          </li>
        </ul>
      </div>

      <el-form-item prop="attachments">
        <div class="flex flex-col gap-4">
          <div class="flex flex-col md:flex-row md:items-center gap-2">
            <FormLabel>Attachment</FormLabel>
            <div class="ml-2 mb-2">
              <el-button
                size="small"
                plain
                class="flex gap-3 cursor-pointer bg-gray-200 rounded-md text-sm px-2 py-1"
                @click="$refs.uploadRef?.$el.querySelector('input').click()"
              >
                <DocumentAdd class="w-4 h-4" />
                <p>Add Attachments</p>
              </el-button>
            </div>
          </div>

          <el-upload
            ref="uploadRef"
            v-model:file-list="fileList"
            :auto-upload="false"
            :multiple="true"
            :show-file-list="false"
            :on-preview="handlePreview"
            :on-change="handleFileChange"
            :accept="getAcceptValue()"
            style="display: none"
          />

          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div
              v-for="(file, index) in fileList"
              :key="file.uid"
              class="flex items-center gap-4 border p-2 rounded-md"
            >
              <div
                class="w-16 h-16 flex items-center justify-center bg-gray-100 rounded"
              >
                <img
                  v-if="file.url || file.raw?.type.startsWith('image')"
                  :src="file.url"
                  class="w-full h-full object-cover rounded cursor-pointer"
                  @click="
                    () => {
                      previewIndex = index;
                      showViewer = true;
                    }
                  "
                />
                <el-icon v-else-if="file.raw?.type?.startsWith('audio')"
                  ><Headset
                /></el-icon>
                <el-icon v-else-if="file.raw?.type?.startsWith('video')"
                  ><VideoCamera
                /></el-icon>
                <el-icon v-else-if="file.raw?.type === 'application/pdf'"
                  ><Document
                /></el-icon>
                <el-icon v-else><Folder /></el-icon>
              </div>

              <div class="flex flex-col gap-1">
                <el-input
                  v-model="file.attachmentName"
                  placeholder="File name"
                  class="w-1/3"
                />
                <el-input
                  v-model="file.description"
                  placeholder="File description"
                  type="textarea"
                />
              </div>

              <el-button circle size="small" @click="removeFile(index)">
                <el-icon><Close /></el-icon>
              </el-button>
            </div>
          </div>
        </div>
      </el-form-item>
    </div>

    <div
      v-if="isUserPermitted(PermissionNames.PAYMENT_PRODUCT_ASSIGN)"
      class="mt-4 px-1"
    >
      <FormLabel class="!mb-1">Payment Items</FormLabel>

      <div v-if="pendingRows.length" class="mt-2 space-y-2">
        <div
          v-for="(row, idx) in pendingRows"
          :key="idx"
          class="p-3 rounded border border-gray-100 space-y-2"
        >
          <el-select
            v-model="row.item_id"
            placeholder="Select a payment item"
            filterable
            clearable
            class="w-full"
            @change="(id) => onPendingItemSelect(idx, id)"
          >
            <el-option
              v-for="item in availableForRow(idx)"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
          <div class="flex gap-2 items-center">
            <el-input
              v-model="row.price"
              type="number"
              size="small"
              placeholder="Price"
              class="flex-1"
              :min="0"
              @wheel="(e) => e.preventDefault()"
            />
            <label
              class="flex items-center gap-1 text-xs text-gray-600 shrink-0 cursor-pointer"
            >
              <el-checkbox v-model="row.is_required" />
              Required
            </label>
            <button
              type="button"
              class="text-xs text-red-500 shrink-0"
              @click="removePendingRow(idx)"
            >
              Remove
            </button>
          </div>
        </div>
      </div>

      <div class="mt-4">
        <button
          type="button"
          class="cursor-pointer text-light-purple underline text-xs w-full flex justify-end dark:text-light-purple-bar"
          @click.prevent="addPendingRow"
        >
          Add Payment Item
        </button>
      </div>
    </div>

    <div class="feature-filter-submit">
      <NavButton
        type="button"
        @click="productStore.setCreateModalVisibility(false)"
      >
        Cancel
      </NavButton>
      <NavButton
        class="bg-light-purple-nav"
        type="submit"
        :is-disabled="!isUserPermitted(PermissionNames.PRODUCT_CREATE)"
      >
        Create
      </NavButton>
    </div>
  </el-form>

  <el-dialog
    v-model="showAddCategoryModal"
    title="Add Category"
    width="400px"
    append-to-body
    :lock-scroll="false"
    class="rounded-lg"
    @close="cancelAddCategory"
  >
    <div class="form-elem-container">
      <FormLabel :required="true">Add Category</FormLabel>
      <el-input
        v-model="newCategoryName"
        clearable
        placeholder="Please input category name"
        @keyup.enter="saveNewCategory"
      />
    </div>
    <div class="feature-filter-submit mt-4">
      <NavButton type="button" @click.prevent="cancelAddCategory">
        Cancel
      </NavButton>
      <NavButton
        class="bg-light-purple-nav"
        type="button"
        @click.prevent="saveNewCategory"
      >
        Save
      </NavButton>
    </div>
  </el-dialog>

  <!-- <AttachmentForm
    :show-header="false"
    :show-attachment-modal="showAttachmentModal"
    :attachable-type="'product'"
    @add-attachment="appendAttachment"
    @close-attachment-modal="closeAttachment"
  /> -->
</template>
